import { FormEvent, useState } from "react";
import { useLocation } from "react-router";
import { cn, getEnvironmentDomain } from "@app/util/helpers";
import Button from "@components/AccessibleComponents/Button";
import TextInput from "@components/AccessibleComponents/TextInput";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import Header from "@components/Header/Header";
import { emarsysCodes } from "@components/Newsletter/types/Emarsys";
import { NEWSLETTER_REQUESTS_RESPONSES } from "@components/Newsletter/types/Newsletter";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UnsubscribeSuccess from "@routes/Newsletter/Unsubscribe/components/UnsubscribeSuccess";
import { APIState, StatusEnums } from "@typings/enums";
import { ENVIRONMENT } from "@util/constant";
import axios from "axios";

const UnsubscribePageContent = (): React.ReactElement => {
  const [status, setStatus] = useState<APIState>({ state: StatusEnums.Idle });
  const ENVIRONMENT_DOMAIN = getEnvironmentDomain(ENVIRONMENT);

  const { search, pathname } = useLocation();
  const url = new URL(`${ENVIRONMENT_DOMAIN}${pathname}${search}`);
  const urlParams = new URLSearchParams(url.search);

  const editionIdParams = urlParams.get("edition");
  const editionId = editionIdParams !== null ? editionIdParams : "";

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setStatus({ state: StatusEnums.Loading });
    const target = event.target as typeof event.target & {
      email: { value: string };
    };

    const request = async () => {
      const response = async (email: string, editionId: string) => {
        const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        if (!emailPattern.test(email))
          return NEWSLETTER_REQUESTS_RESPONSES.INVALIDEMAIL;

        const selectedKeys = editionId;
        const apiPayload = {
          service: "emarsys",
          payload: {
            email: email,
            selectedKeys: [selectedKeys],
            isUnsubscribe: true,
          },
        };

        const response = await axios.post("/_plat/api/v1/bff", apiPayload);

        // Guard clauses
        if (response.status !== 200) return NEWSLETTER_REQUESTS_RESPONSES.ERROR;
        if (response.data.statusCode !== 200)
          return NEWSLETTER_REQUESTS_RESPONSES.ERROR;
        if (!emarsysCodes.includes(response.data.body))
          return NEWSLETTER_REQUESTS_RESPONSES.ERROR;

        return (
          Object.values(NEWSLETTER_REQUESTS_RESPONSES).find(
            (emarsysResponse) =>
              emarsysResponse.emarsysCode === response.data.body
          ) || NEWSLETTER_REQUESTS_RESPONSES.ERROR
        );
      };

      await response(target.email.value, editionId).then(
        ({ status, message }) => {
          if (status === "error" && message === "Something went wrong!") {
            setStatus({ state: StatusEnums.Error, error: message });
          }

          if (status === "error") {
            setStatus({
              state: StatusEnums.Error,
              error: "Please enter a valid email address.",
            });
          }

          if (status === "success") {
            // navigateTo(`/unsubscription?edition=${editionId}&sc=1`);
            setStatus({ state: StatusEnums.Success });
          }
        }
      );
    };

    request();
  };

  return (
    <div className="w-full" data-testid="unsubscribe-page-component">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <Container rootClassName="py-3">
        {status.state === StatusEnums.Success ? (
          <UnsubscribeSuccess />
        ) : (
          <>
            {status.state === StatusEnums.Error ? (
              <div className="mt-1 rounded-md bg-red-100 p-8 font-poppins text-xs font-semibold text-red-300">
                <h2 className="border-b-1 inline">Error message</h2>
                <p>{status.error}</p>
              </div>
            ) : null}

            <h3 className="mb-2 pb-3 font-poppins lg:text-8xl">
              Unsubscribe from this newsletter
            </h3>

            <form
              className={cn(
                "ml-auto w-full items-center justify-between pb-1",
                status.state === StatusEnums.Error
                  ? "border-red"
                  : "border-gray-850"
              )}
              onSubmit={handleSubmit}
            >
              <label
                htmlFor={`input-email`}
                className="font-poppins text-base text-[#212529]"
              >
                Please enter your email address
              </label>

              <TextInput
                className={cn(
                  "mb-3 mt-2 w-full rounded-md border px-3 py-1.5 font-poppins",
                  status.state === StatusEnums.Error
                    ? "!border-red"
                    : "text-gray-850"
                )}
                name="email"
                type="email"
                inputMode="email"
                defaultValue={""}
                required
                size={60}
                maxLength={254}
              />

              <Button
                className="rounded bg-[#0D6EFD] px-4 py-2 font-poppins text-base font-normal text-white"
                type="submit"
              >
                {status.state === StatusEnums.Loading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  "Unsubscribe"
                )}
              </Button>
            </form>
          </>
        )}
      </Container>

      <Ad adType="prestitial" />
    </div>
  );
};

export default UnsubscribePageContent;
